import { useState } from "react";
import type { Job, Issue } from "@/data/types";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { deriveReadinessFromJob, getCanonicalInstallReadiness, type CanonicalReadinessStatus } from "@/data/readiness";
import { useAppData } from "@/data/AppDataContext";
import { getKit } from "@/data/materialKit";
import { canMarkJobReady, markJobReady, isExecutionReady, isLockStale } from "@/data/executionReadyLock";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, AlertTriangle, Shield, Package, ShieldAlert, FileCheck, Lock,
} from "lucide-react";

export type ReadinessGateStatus = "Ready" | "Partially Ready" | "Not Ready";

export function getReadinessGateStatus(
  job: Job,
  _openIssues: Issue[],
  buildReviewEvents: BuildReviewEvent[],
): {
  status: ReadinessGateStatus;
  permit: string;
  materials: string;
  missingRequirements: string[];
} {
  const derived = deriveReadinessFromJob(job, buildReviewEvents);
  const all = [derived.permit, derived.materials, derived.crew, derived.schedule, derived.site, derived.financial, derived.buildReview];

  const missing: string[] = all
    .filter(i => i.status !== "ready")
    .map(i => `${i.label}: ${i.shortText}`);

  let status: ReadinessGateStatus;
  if (derived.overall === "ready") status = "Ready";
  else if (derived.overall === "blocked") status = "Not Ready";
  else status = derived.readyCount >= 5 ? "Partially Ready" : "Not Ready";

  return {
    status,
    permit: derived.permit.shortText,
    materials: derived.materials.shortText,
    missingRequirements: missing,
  };
}

/* ── Display helpers ── */
function getStatusDisplay(status: CanonicalReadinessStatus): { color: string; icon: React.ElementType } {
  if (status === "ready") return { color: "text-success bg-success/10 border-success/30", icon: CheckCircle };
  if (status === "blocked") return { color: "text-destructive bg-destructive/10 border-destructive/30", icon: XCircle };
  return { color: "text-warning bg-warning/10 border-warning/30", icon: AlertTriangle };
}

const GATE_CONFIG: Record<ReadinessGateStatus, { color: string; bg: string; icon: React.ElementType }> = {
  Ready: { color: "text-success", bg: "border-success/30 bg-success/5", icon: CheckCircle },
  "Partially Ready": { color: "text-warning", bg: "border-warning/30 bg-warning/5", icon: AlertTriangle },
  "Not Ready": { color: "text-destructive", bg: "border-destructive/30 bg-destructive/5", icon: ShieldAlert },
};

interface Props {
  job: Job;
  issues: Issue[];
}

interface PanelProps extends Props {
  /** When true, hide the Mark Ready CTA (e.g. Contractor Portal). */
  readOnly?: boolean;
  /** Operator name to record on lock. Defaults to "Operator". */
  operatorName?: string;
}

export function ReadinessGatingPanel({ job, issues, readOnly = false, operatorName = "Operator" }: PanelProps) {
  const { buildReviewEvents, materialKits, payProfiles, contractors, setJobs } = useAppData();
  const kit = getKit(materialKits, job.id);
  const openIssues = issues.filter((i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));
  const readiness = getCanonicalInstallReadiness(job, openIssues, buildReviewEvents, kit);
  const derived = deriveReadinessFromJob(job, buildReviewEvents, kit);
  const verdict = canMarkJobReady({ job, kit, payProfiles, contractors, buildReviewEvents });
  const locked = isExecutionReady(job);
  const stale = locked && isLockStale(job, { job, kit, payProfiles, contractors, buildReviewEvents });
  const [marking, setMarking] = useState(false);
  const displayStatus: ReadinessGateStatus =
    readiness.status === "Ready for Install"
      ? "Ready"
      : readiness.status === "Partially Ready"
        ? "Partially Ready"
        : "Not Ready";
  const gateConfig = GATE_CONFIG[displayStatus];
  const GateIcon = gateConfig.icon;
  const permitCfg = getStatusDisplay(derived.permit.status);
  const PermitIcon = permitCfg.icon;
  const matCfg = getStatusDisplay(derived.materials.status);
  const MatIcon = matCfg.icon;
  const specCfg = getStatusDisplay(derived.jobSpec.status);
  const SpecIcon = specCfg.icon;

  const handleMarkReady = () => {
    setMarking(true);
    const result = markJobReady(job, operatorName, verdict);
    if (!result.ok) {
      toast.error("Cannot mark job ready", { description: result.reason });
      setMarking(false);
      return;
    }
    setJobs((prev) => prev.map((j) => (j.id === job.id ? result.job : j)));
    toast.success("Job locked for execution");
    setMarking(false);
  };

  return (
    <div className="space-y-3">
      {/* Overall readiness gate */}
      <div className={`rounded-lg border-2 p-3 ${gateConfig.bg}`}>
        <div className="flex items-center justify-between mb-2">
          <div className={`flex items-center gap-2 ${gateConfig.color}`}>
            <GateIcon className="h-4 w-4" />
            <span className="text-sm font-bold">{displayStatus}</span>
          </div>
          <span className={`text-sm font-bold ${gateConfig.color}`}>{readiness.score}%</span>
        </div>
        <Progress
          value={readiness.score}
          className={`h-2 ${
            displayStatus === "Ready"
              ? "[&>div]:bg-success"
              : displayStatus === "Partially Ready"
                ? "[&>div]:bg-warning"
                : "[&>div]:bg-destructive"
          }`}
        />
        {displayStatus !== "Ready" && (
          <p className="text-[10px] text-muted-foreground mt-2">
            {displayStatus === "Not Ready"
              ? "This job can be assigned or rescheduled, but cannot be locked for execution until these factors clear."
              : "Some requirements are incomplete. Scheduling with caution is recommended."}
          </p>
        )}
      </div>

      {/* Requirement cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* JobSpec */}
        <div className={`rounded-lg border px-3 py-2.5 ${specCfg.color}`}>
          <div className="flex items-center gap-2 mb-1">
            <FileCheck className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em]">JobSpec</span>
          </div>
          <div className="flex items-center gap-1.5">
            <SpecIcon className="h-3 w-3" />
            <span className="text-xs font-semibold">{derived.jobSpec.shortText}</span>
          </div>
          {derived.jobSpec.description && (
            <p className="text-[10px] text-muted-foreground mt-1">{derived.jobSpec.description}</p>
          )}
        </div>

        {/* Permit */}
        <div className={`rounded-lg border px-3 py-2.5 ${permitCfg.color}`}>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em]">Permitting</span>
          </div>
          <div className="flex items-center gap-1.5">
            <PermitIcon className="h-3 w-3" />
            <span className="text-xs font-semibold">{derived.permit.shortText}</span>
          </div>
          {derived.permit.description && (
            <p className="text-[10px] text-muted-foreground mt-1">{derived.permit.description}</p>
          )}
        </div>

        {/* Materials */}
        <div className={`rounded-lg border px-3 py-2.5 ${matCfg.color}`}>
          <div className="flex items-center gap-2 mb-1">
            <Package className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em]">Materials</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MatIcon className="h-3 w-3" />
            <span className="text-xs font-semibold">{derived.materials.shortText}</span>
          </div>
          {derived.materials.description && (
            <p className="text-[10px] text-muted-foreground mt-1">{derived.materials.description}</p>
          )}
        </div>
      </div>

      {/* Missing requirements list — with explainability */}
      {verdict.reasons.length > 0 && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/[0.02] px-3 py-2.5">
          <p className="text-[10px] font-bold text-destructive uppercase tracking-[0.12em] mb-1.5">
            Execution Lock Requirements ({verdict.reasons.length})
          </p>
          <ul className="space-y-1">
            {verdict.reasons.map((req, i) => (
              <li key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <XCircle className="h-2.5 w-2.5 text-destructive shrink-0" />
                {req}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Execution Ready lock ── */}
      {!readOnly && (
        locked ? (
          <div
            className={`rounded-lg border px-3 py-2.5 flex items-center gap-2 ${
              stale
                ? "border-warning/40 bg-warning/10 text-warning"
                : "border-success/30 bg-success/10 text-success"
            }`}
          >
            {stale ? <AlertTriangle className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            <div>
              <p className="text-xs font-bold">
                {stale ? "Lock Stale — recorded factor regressed" : "Ready for Execution — Locked"}
              </p>
              {job.executionReady && (
                <p className="text-[10px] text-muted-foreground">
                  Marked by {job.executionReady.markedBy} · {new Date(job.executionReady.markedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            className="w-full"
            disabled={!verdict.ok || marking}
            onClick={handleMarkReady}
          >
            <Lock className="h-3.5 w-3.5 mr-1.5" />
            {verdict.ok ? "Mark Ready for Execution" : "Resolve execution blockers to lock"}
          </Button>
        )
      )}
      {readOnly && locked && (
        <div
          className={`rounded-lg border px-3 py-2 flex items-center gap-2 text-xs font-semibold ${
            stale
              ? "border-warning/40 bg-warning/10 text-warning"
              : "border-success/30 bg-success/10 text-success"
          }`}
        >
          {stale ? <AlertTriangle className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {stale ? "Lock Stale" : "Ready for Execution — Locked"}
        </div>
      )}
    </div>
  );
}
